/**
 * AdminDashboard — lightweight MVP admin overview.
 *
 * Renders an at-a-glance operational dashboard for the platform owner:
 *   • User metrics (totals + recent signups)
 *   • Auth/session monitoring (logins, failures, password-reset health)
 *   • Beta feedback / support tickets (totals + recent items)
 *   • Smart Nutrition engagement (top cuisines, top meals)
 *   • Email delivery health
 *
 * Reuses existing /api/admin/* infrastructure. Role-gated: non-admins are
 * redirected to /dashboard at the App-router level, plus a defensive
 * client-side check here in case the gate is bypassed.
 *
 * Intentionally English-only — internal admin tool, not part of the
 * Phase-2 i18n surface. (Husky pre-commit lint scope only enforces i18n
 * on launch-facing pages.)
 */
/* eslint-disable */

import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft, Users, UserPlus, KeyRound, ShieldAlert, MessageSquareWarning,
  LifeBuoy, Stethoscope, Salad, Truck, Mail, RefreshCw, BarChart3, Activity,
  LogOut, ExternalLink, CheckCircle2, AlertTriangle, Loader2, ServerCog, Megaphone,
} from 'lucide-react';
import { VerifyDiagnoseCard } from '@/components/admin/VerifyDiagnoseCard';
import { BetaToolsCard } from '@/components/admin/BetaToolsCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stat = ({ icon: Icon, label, value, hint, tone = 'slate', testid }) => {
  const tones = {
    slate: 'from-slate-100 to-slate-50 text-slate-700',
    emerald: 'from-emerald-100 to-emerald-50 text-emerald-700',
    rose: 'from-rose-100 to-rose-50 text-rose-700',
    amber: 'from-amber-100 to-amber-50 text-amber-700',
    indigo: 'from-indigo-100 to-indigo-50 text-indigo-700',
    sky: 'from-sky-100 to-sky-50 text-sky-700',
  };
  return (
    <Card className="border border-gray-100 rounded-2xl shadow-sm" data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className={`inline-flex items-center justify-center w-9 h-9 rounded-xl bg-gradient-to-br ${tones[tone]}`}>
            {Icon && <Icon className="h-4 w-4" />}
          </span>
        </div>
        <p className="text-2xl font-semibold text-gray-900 tabular-nums leading-none">{value}</p>
        <p className="text-xs text-gray-600 mt-1">{label}</p>
        {hint && <p className="text-[11px] text-gray-400 mt-1">{hint}</p>}
      </CardContent>
    </Card>
  );
};

const Section = ({ icon: Icon, title, subtitle, children, action }) => (
  <section className="mb-6" data-testid={`admin-section-${(title || '').toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        {Icon && (
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-100 to-violet-50 text-indigo-700">
            <Icon className="h-4 w-4" />
          </span>
        )}
        <div>
          <h2 className="text-base font-semibold text-gray-900 leading-tight">{title}</h2>
          {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
        </div>
      </div>
      {action}
    </div>
    {children}
  </section>
);

const Pill = ({ children, tone = 'slate' }) => {
  const tones = {
    slate: 'bg-slate-100 text-slate-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    rose: 'bg-rose-100 text-rose-700',
    amber: 'bg-amber-100 text-amber-700',
    sky: 'bg-sky-100 text-sky-700',
  };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${tones[tone]}`}>{children}</span>;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const truncate = (s, n = 80) => (typeof s === 'string' && s.length > n ? s.slice(0, n) + '…' : (s || ''));

// Compact, dashboard-friendly subsystem health row.
const SubsystemRow = ({ name, label, data }) => {
  const status = data?.status || 'skipped';
  // 'warning' is a soft-pass — the subsystem is functioning but the
  // operator should clean something up (e.g. stale env-var panel value
  // that's being overridden at runtime). Rendered in amber to draw the
  // eye without alarming.
  const hasWarning = status === 'pass' && data?.warning;
  const tone = {
    pass:    hasWarning
      ? { wrap: 'bg-amber-50 text-amber-900 border-amber-200', icon: AlertTriangle }
      : { wrap: 'bg-emerald-50 text-emerald-800 border-emerald-200', icon: CheckCircle2 },
    fail:    { wrap: 'bg-rose-50 text-rose-800 border-rose-200', icon: AlertTriangle },
    skipped: { wrap: 'bg-slate-50 text-slate-600 border-slate-200', icon: AlertTriangle },
  }[status] || { wrap: 'bg-slate-50 text-slate-600 border-slate-200', icon: AlertTriangle };
  const Icon = tone.icon;
  const displayStatus = hasWarning ? 'pass · warn' : status;
  // Effective config block (currently set on the resend_email probe)
  // — surfaces the runtime sender/reply-to/support-inbox so the admin
  // can confirm the active production values without leaving the page.
  const eff = data?.effective;
  return (
    <div
      className={`flex flex-col gap-1.5 px-3 py-2.5 rounded-xl border ${tone.wrap}`}
      data-testid={`subsystem-${name}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <Icon className="h-3.5 w-3.5 flex-shrink-0" />
            <span className="text-sm font-semibold">{label}</span>
            <span
              className="inline-flex items-center text-[10px] uppercase tracking-wide font-bold ml-1"
              data-testid={`subsystem-${name}-status`}
            >
              {displayStatus}
            </span>
          </div>
          <p className="text-[11px] mt-0.5 opacity-80 break-words">{data?.detail || '—'}</p>
        </div>
        <span className="text-[10px] font-mono opacity-60 whitespace-nowrap">
          {data?.latency_ms != null ? `${data.latency_ms}ms` : ''}
        </span>
      </div>
      {/* Effective config readout (for the resend_email probe). Helps
          the admin confirm runtime values without opening the Emergent
          Secrets panel. */}
      {eff && (
        <div className="mt-1 text-[10px] font-mono opacity-75 grid grid-cols-1 gap-0.5" data-testid={`subsystem-${name}-effective`}>
          {eff.sender && <span>sender · {eff.sender}</span>}
          {eff.reply_to && <span>reply_to · {eff.reply_to}</span>}
          {eff.support_inbox && <span>support_inbox · {eff.support_inbox}</span>}
          {eff.support_backup && <span>backup · {eff.support_backup}</span>}
        </div>
      )}
    </div>
  );
};

const SystemHealthCard = ({ health, loading, onRefresh }) => {
  const overall = health?.overall || 'unknown';
  const failures = health?.failures || [];
  const subsystems = health?.subsystems || {};
  const checked = health?.checked_at;
  const overallTone = overall === 'pass'
    ? 'bg-emerald-100 text-emerald-800'
    : overall === 'fail'
      ? 'bg-rose-100 text-rose-800'
      : 'bg-slate-100 text-slate-700';
  return (
    <Card className="border border-gray-100 rounded-2xl shadow-sm" data-testid="admin-system-health-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="text-sm flex items-center gap-2">
              Subsystem health
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${overallTone}`}
                data-testid="system-health-overall"
              >
                {loading ? 'checking…' : overall}
              </span>
            </CardTitle>
            <CardDescription className="text-xs">
              {checked ? `Last checked ${fmtDate(checked)}` : 'Live probe of DB, Resend, Google auth, support pipeline'}
              {health?.cached ? ' · cached' : ''}
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            disabled={loading}
            data-testid="system-health-recheck-btn"
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            <span className="ml-1.5">Re-check</span>
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading && !health ? (
          <p className="text-xs text-gray-500">Running probes…</p>
        ) : (
          <>
            {health?.latest_failure && (
              <div
                className="mb-3 rounded-xl bg-rose-50 border border-rose-200 px-3 py-2"
                data-testid="latest-failure"
              >
                <p className="text-[11px] uppercase tracking-wide text-rose-700 font-semibold">Latest failure</p>
                <p className="text-xs text-rose-900 mt-0.5">
                  <span className="font-semibold">{health.latest_failure.subsystem}:</span>{' '}
                  {health.latest_failure.detail}
                </p>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <SubsystemRow name="database" label="Database (MongoDB)" data={subsystems.database} />
              <SubsystemRow name="resend_email" label="Email delivery (Resend)" data={subsystems.resend_email} />
              <SubsystemRow name="google_auth" label="Google sign-in" data={subsystems.google_auth} />
              <SubsystemRow name="support_tickets" label="Support tickets pipeline" data={subsystems.support_tickets} />
              <SubsystemRow name="critical_config" label="Critical config (env vars)" data={subsystems.critical_config} />
            </div>
            {failures.length > 0 && (
              <p className="text-[11px] text-rose-600 mt-3" data-testid="failures-line">
                {failures.length} subsystem{failures.length === 1 ? '' : 's'} failing — investigate before launch.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [overview, setOverview] = useState(null);
  const [feedback, setFeedback] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [logins, setLogins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  // Subsystem deep-health probe — independent of the main loader because
  // it pings Resend + Mongo + the upstream Google auth service and can
  // take ~300ms.
  const [systemHealth, setSystemHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(true);

  // Defensive client-side role gate (App.js redirect is the primary control).
  useEffect(() => {
    if (user && user !== null && user.role !== 'admin') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const [ov, fb, tk, lg] = await Promise.all([
          axios.get(`${API}/admin/overview`, { withCredentials: true }),
          axios.get(`${API}/admin/feedback?limit=10`, { withCredentials: true }),
          axios.get(`${API}/admin/support-tickets?limit=10`, { withCredentials: true }),
          axios.get(`${API}/admin/login-events?limit=10`, { withCredentials: true }),
        ]);
        if (cancelled) return;
        setOverview(ov.data);
        setFeedback(fb.data?.items || []);
        setTickets(tk.data?.items || []);
        setLogins(lg.data?.items || []);
        // System health probe — own request because it's higher-latency
        // (~300ms when Resend domains endpoint is probed). We let it
        // resolve asynchronously rather than block the rest of the
        // dashboard render.
        setHealthLoading(true);
        axios.get(`${API}/admin/system-health`, { withCredentials: true })
          .then((res) => { if (!cancelled) setSystemHealth(res.data); })
          .catch(() => { if (!cancelled) setSystemHealth({ overall: 'fail', error: true }); })
          .finally(() => { if (!cancelled) setHealthLoading(false); });
      } catch (e) {
        if (cancelled) return;
        const status = e?.response?.status;
        if (status === 401) setError('Please sign in as an admin.');
        else if (status === 403) setError('Admin access required.');
        else setError('Could not load admin dashboard.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [refreshTick]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (loading && !overview) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-sm text-gray-500">Loading admin dashboard…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 px-6">
        <ShieldAlert className="h-10 w-10 text-rose-500 mb-3" />
        <p className="text-sm font-medium text-gray-900 mb-2">{error}</p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/dashboard')} data-testid="admin-back-dashboard">Back to app</Button>
          <Button onClick={handleLogout} data-testid="admin-logout-error">Sign out</Button>
        </div>
      </div>
    );
  }

  const u = overview?.users || {};
  const a = overview?.auth || {};
  const e = overview?.engagement || {};
  const n = overview?.nutrition || {};
  const eh = overview?.email_health || {};

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 md:top-16 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} aria-label="Back to app" data-testid="admin-back-btn">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-bold text-gray-900 leading-tight">Admin Dashboard</h1>
              <p className="text-[11px] text-gray-500">MVP operational overview · {overview?.generated_at ? fmtDate(overview.generated_at) : '—'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setRefreshTick((t) => t + 1)} data-testid="admin-refresh-btn">
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
              Refresh
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="admin-logout-btn">
              <LogOut className="h-3.5 w-3.5 mr-1.5" />
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 pb-24">
        {/* SYSTEM STATUS — deep health check across DB, Resend, Google auth, support */}
        <Section icon={ServerCog} title="System Status" subtitle="Live subsystem health · refreshed each dashboard load">
          <SystemHealthCard health={systemHealth} loading={healthLoading} onRefresh={() => {
            setHealthLoading(true);
            axios.get(`${API}/admin/system-health?force=true`, { withCredentials: true })
              .then((res) => setSystemHealth(res.data))
              .catch(() => setSystemHealth({ overall: 'fail', error: true }))
              .finally(() => setHealthLoading(false));
          }} />
        </Section>

        {/* EMAIL VERIFICATION DIAGNOSE & UNBLOCK */}
        <Section icon={ShieldAlert} title="Email Verification" subtitle="Inspect a user's verification token state, or manually unblock stuck testers">
          <VerifyDiagnoseCard />
        </Section>

        {/* BETA LAUNCH TOOLS — audit, test sends, reminder pipeline health */}
        <Section icon={Activity} title="Beta Launch Tools" subtitle="Audit chain, test verification email, reminder pipeline health">
          <BetaToolsCard />
        </Section>

        {/* USER METRICS */}
        <Section icon={Users} title="User Metrics" subtitle="Registration and growth signal">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Stat icon={Users} label="Total registered users" value={u.total ?? '—'} tone="indigo" testid="stat-users-total" />
            <Stat icon={UserPlus} label="New signups · last 7d" value={u.signups_7d ?? '—'} tone="emerald" testid="stat-signups-7d" />
            <Stat icon={UserPlus} label="New signups · last 30d" value={u.signups_30d ?? '—'} tone="sky" testid="stat-signups-30d" />
          </div>
        </Section>

        {/* SESSION & LOGIN MONITORING */}
        <Section icon={KeyRound} title="Session & Login Monitoring" subtitle="Auth attempts and password-reset health">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <Stat icon={Activity} label="Login attempts · 24h" value={a.login_attempts_24h ?? 0} tone="slate" testid="stat-logins-24h" />
            <Stat icon={ShieldAlert} label="Failed logins · 24h" value={a.login_failures_24h ?? 0} hint={`${a.login_failures_7d ?? 0} in last 7d`} tone={a.login_failures_24h > 5 ? 'rose' : 'slate'} testid="stat-login-failures-24h" />
            <Stat icon={KeyRound} label="Password resets requested · 7d" value={a.reset_requested_7d ?? 0} tone="amber" testid="stat-reset-requested-7d" />
            <Stat icon={KeyRound} label="Password resets completed · 7d" value={a.reset_completed_7d ?? 0} hint={`${a.reset_unused_7d ?? 0} unused`} tone="emerald" testid="stat-reset-completed-7d" />
          </div>

          <Card className="border border-gray-100 rounded-2xl shadow-sm" data-testid="admin-table-logins">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Recent login attempts</CardTitle>
              <CardDescription className="text-xs">Last 10 events — useful for spotting brute-force or beta access friction</CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              {logins.length === 0 ? (
                <p className="text-xs text-gray-500">No login attempts yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="text-gray-500 text-left">
                      <tr>
                        <th className="font-medium py-2 pr-3">When</th>
                        <th className="font-medium py-2 pr-3">Email</th>
                        <th className="font-medium py-2 pr-3">Result</th>
                        <th className="font-medium py-2 pr-3 hidden md:table-cell">IP</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-700">
                      {logins.map((row, i) => (
                        <tr key={i} className="border-t border-gray-50">
                          <td className="py-2 pr-3 whitespace-nowrap">{fmtDate(row.created_at)}</td>
                          <td className="py-2 pr-3 truncate max-w-[180px]">{row.email || '—'}</td>
                          <td className="py-2 pr-3">
                            {row.success
                              ? <Pill tone="emerald">success</Pill>
                              : <Pill tone="rose">failed</Pill>}
                          </td>
                          <td className="py-2 pr-3 hidden md:table-cell text-gray-500">{row.ip || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </Section>

        {/* BETA FEEDBACK TRACKING */}
        <Section
          icon={MessageSquareWarning}
          title="Beta Feedback Tracking"
          subtitle="What users are reporting"
          action={
            <a
              href={`${API}/admin/feedback/export.csv`}
              className="text-xs text-indigo-600 hover:text-indigo-700 hover:underline inline-flex items-center gap-1"
              data-testid="admin-export-feedback-csv"
            >
              <BarChart3 className="h-3.5 w-3.5" />
              Export feedback CSV
            </a>
          }
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <Stat icon={MessageSquareWarning} label="Feedback · total" value={e.feedback_total ?? 0} hint={`${e.feedback_new ?? 0} new · ${e.feedback_7d ?? 0} last 7d`} tone="indigo" testid="stat-feedback-total" />
            <Stat icon={LifeBuoy} label="Support tickets" value={e.support_total ?? 0} hint={`${e.support_7d ?? 0} last 7d`} tone="amber" testid="stat-support-total" />
            <Stat icon={Stethoscope} label="Specialist requests" value={e.specialist_total ?? 0} hint={`${e.specialist_7d ?? 0} last 7d`} tone="rose" testid="stat-specialist-total" />
            <Stat icon={Truck} label="Vendor partners" value={n.vendor_partners_total ?? 0} tone="emerald" testid="stat-vendor-partners-total" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Card className="border border-gray-100 rounded-2xl shadow-sm" data-testid="admin-table-feedback">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Recent feedback</CardTitle>
                <CardDescription className="text-xs">Last 10 submissions</CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                {feedback.length === 0 ? (
                  <p className="text-xs text-gray-500">No feedback yet.</p>
                ) : (
                  <ul className="space-y-2.5">
                    {feedback.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs">
                        <Pill tone={f.status === 'new' ? 'sky' : 'slate'}>{f.status || 'new'}</Pill>
                        <div className="min-w-0 flex-1">
                          <p className="text-gray-800 truncate"><span className="font-medium">{f.type || 'feedback'}</span> · {f.user_email || '—'}</p>
                          <p className="text-gray-500">{truncate(f.feedback_text, 120) || <em className="text-gray-400">no text</em>}</p>
                          <p className="text-[10px] text-gray-400 mt-0.5">{fmtDate(f.created_at)}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            <Card className="border border-gray-100 rounded-2xl shadow-sm" data-testid="admin-table-tickets">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <CardTitle className="text-sm">Recent support tickets</CardTitle>
                    <CardDescription className="text-xs">Last 10 tickets · tap to view</CardDescription>
                  </div>
                  <Link
                    to="/admin/support-tickets"
                    className="text-[11px] text-emerald-700 hover:underline whitespace-nowrap mt-0.5"
                    data-testid="view-all-tickets-link"
                  >
                    View all →
                  </Link>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {tickets.length === 0 ? (
                  <p className="text-xs text-gray-500">No support tickets yet.</p>
                ) : (
                  <ul className="space-y-1">
                    {tickets.map((t) => (
                      <li key={t.id || t._id}>
                        {/* Make each row a real Link — fixes the
                            "tapping does nothing" bug. Lands on the full
                            triage page with the dialog auto-opened for
                            this ticket via ?id=<uuid>. */}
                        <Link
                          to={`/admin/support-tickets?id=${encodeURIComponent(t.id || '')}`}
                          className="flex items-start gap-2 text-xs px-2 py-2 -mx-2 rounded-lg hover:bg-slate-50 active:bg-slate-100 transition-colors"
                          data-testid={`recent-ticket-row-${t.id || 'unknown'}`}
                        >
                          <Pill tone={t.status === 'open' || !t.status ? 'amber' : 'slate'}>{t.status || 'open'}</Pill>
                          <div className="min-w-0 flex-1">
                            <p className="text-gray-800 truncate">
                              <span className="font-medium">{t.title || t.subject || t.category || 'Ticket'}</span>
                              {' · '}
                              <span className="text-gray-600">{t.contact_email || t.user_email || '—'}</span>
                            </p>
                            <p className="text-gray-500 truncate">
                              {truncate(t.description || t.message, 120) || <em className="text-gray-400">no text</em>}
                            </p>
                            <p className="text-[10px] text-gray-400 mt-0.5">{fmtDate(t.created_at)}</p>
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>
        </Section>

        {/* SMART NUTRITION MONITORING */}
        <Section icon={Salad} title="Smart Nutrition Engagement" subtitle="Top picks · last 30 days">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Card className="border border-gray-100 rounded-2xl shadow-sm" data-testid="admin-table-cuisines">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Top cuisines</CardTitle>
                <CardDescription className="text-xs">By interaction count</CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                {(n.top_cuisines || []).length === 0 ? (
                  <p className="text-xs text-gray-500">No cuisine interactions in the last 30 days.</p>
                ) : (
                  <ul className="space-y-1.5">
                    {n.top_cuisines.map((c, i) => (
                      <li key={i} className="flex items-center justify-between text-xs">
                        <span className="text-gray-700">{c.cuisine}</span>
                        <Pill tone="emerald">{c.count}</Pill>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            <Card className="border border-gray-100 rounded-2xl shadow-sm" data-testid="admin-table-meals">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Top meals</CardTitle>
                <CardDescription className="text-xs">Most selected</CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                {(n.top_meals || []).length === 0 ? (
                  <p className="text-xs text-gray-500">No meal interactions in the last 30 days.</p>
                ) : (
                  <ul className="space-y-1.5">
                    {n.top_meals.map((m, i) => (
                      <li key={i} className="flex items-center justify-between text-xs">
                        <span className="text-gray-700 truncate pr-2">{m.meal}</span>
                        <Pill tone="indigo">{m.count}</Pill>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>
        </Section>

        {/* SYSTEM HEALTH */}
        <Section icon={Activity} title="System Health" subtitle="Email delivery and operational signal">
          <Card className="border border-gray-100 rounded-2xl shadow-sm" data-testid="admin-email-health">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><Mail className="h-4 w-4" /> Email events · last 7 days</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {Object.keys(eh).length === 0 ? (
                <p className="text-xs text-gray-500">No email events in the last 7 days.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {Object.entries(eh).map(([state, count]) => (
                    <Pill key={state} tone={
                      state === 'delivered' ? 'emerald'
                      : state.includes('bounce') || state.includes('fail') ? 'rose'
                      : 'slate'
                    }>
                      {state} · {count}
                    </Pill>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </Section>

        {/* DEEPER VIEWS */}
        <Section icon={BarChart3} title="Deeper Views" subtitle="Existing detailed analytics">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Link to="/admin/users" className="block" data-testid="link-admin-users">
              <Card className="border border-gray-100 rounded-2xl shadow-sm hover:shadow-md hover:border-indigo-200 transition-all">
                <CardContent className="p-4 flex items-center gap-3">
                  <Users className="h-4 w-4 text-indigo-600" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900">Users</p>
                    <p className="text-[11px] text-gray-500">Browse · search · export · per-user actions</p>
                  </div>
                  <ExternalLink className="h-3.5 w-3.5 text-gray-400" />
                </CardContent>
              </Card>
            </Link>
            <Link to="/admin/email-trace" className="block" data-testid="link-admin-email-trace">
              <Card className="border border-gray-100 rounded-2xl shadow-sm hover:shadow-md hover:border-indigo-200 transition-all">
                <CardContent className="p-4 flex items-center gap-3">
                  <Mail className="h-4 w-4 text-rose-600" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900">Email Trace</p>
                    <p className="text-[11px] text-gray-500">Per-user delivery diagnostics</p>
                  </div>
                  <ExternalLink className="h-3.5 w-3.5 text-gray-400" />
                </CardContent>
              </Card>
            </Link>
            <Link to="/admin/analytics" className="block" data-testid="link-admin-analytics">
              <Card className="border border-gray-100 rounded-2xl shadow-sm hover:shadow-md hover:border-indigo-200 transition-all">
                <CardContent className="p-4 flex items-center gap-3">
                  <BarChart3 className="h-4 w-4 text-indigo-600" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900">Analytics</p>
                    <p className="text-[11px] text-gray-500">Sessions, events, features</p>
                  </div>
                  <ExternalLink className="h-3.5 w-3.5 text-gray-400" />
                </CardContent>
              </Card>
            </Link>
            <Link to="/admin/insights" className="block" data-testid="link-admin-insights">
              <Card className="border border-gray-100 rounded-2xl shadow-sm hover:shadow-md hover:border-indigo-200 transition-all">
                <CardContent className="p-4 flex items-center gap-3">
                  <Activity className="h-4 w-4 text-emerald-600" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900">Engagement Insights</p>
                    <p className="text-[11px] text-gray-500">Wellness tips · cohorts · waitlist</p>
                  </div>
                  <ExternalLink className="h-3.5 w-3.5 text-gray-400" />
                </CardContent>
              </Card>
            </Link>
            <Link to="/admin/email-analytics" className="block" data-testid="link-admin-email-analytics">
              <Card className="border border-gray-100 rounded-2xl shadow-sm hover:shadow-md hover:border-indigo-200 transition-all">
                <CardContent className="p-4 flex items-center gap-3">
                  <Mail className="h-4 w-4 text-amber-600" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900">Email Analytics</p>
                    <p className="text-[11px] text-gray-500">Deliverability and provider state</p>
                  </div>
                  <ExternalLink className="h-3.5 w-3.5 text-gray-400" />
                </CardContent>
              </Card>
            </Link>
            <Link to="/admin/support-tickets" className="block" data-testid="link-admin-support-tickets">
              <Card className="border border-gray-100 rounded-2xl shadow-sm hover:shadow-md hover:border-indigo-200 transition-all">
                <CardContent className="p-4 flex items-center gap-3">
                  <LifeBuoy className="h-4 w-4 text-amber-600" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900">Support Tickets</p>
                    <p className="text-[11px] text-gray-500">Triage · search · filter · resolve · notes</p>
                  </div>
                  <ExternalLink className="h-3.5 w-3.5 text-gray-400" />
                </CardContent>
              </Card>
            </Link>
            <Link to="/admin/health-alerts" className="block" data-testid="link-admin-health-alerts">
              <Card className="border border-gray-100 rounded-2xl shadow-sm hover:shadow-md hover:border-indigo-200 transition-all">
                <CardContent className="p-4 flex items-center gap-3">
                  <Megaphone className="h-4 w-4 text-amber-600" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900">Global Health Alerts</p>
                    <p className="text-[11px] text-gray-500">Publish WHO / NHS / global advisories</p>
                  </div>
                  <ExternalLink className="h-3.5 w-3.5 text-gray-400" />
                </CardContent>
              </Card>
            </Link>
            <Link to="/admin/feedback-analytics" className="block" data-testid="link-admin-feedback-analytics">
              <Card className="border border-gray-100 rounded-2xl shadow-sm hover:shadow-md hover:border-indigo-200 transition-all">
                <CardContent className="p-4 flex items-center gap-3">
                  <BarChart3 className="h-4 w-4 text-amber-600" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900">Beta Feedback Analytics</p>
                    <p className="text-[11px] text-gray-500">Impressions · CTR · ratings · submissions</p>
                  </div>
                  <ExternalLink className="h-3.5 w-3.5 text-gray-400" />
                </CardContent>
              </Card>
            </Link>
            {/* Send-test-reminder card — one-click verification of the
                reminder pipeline. Bypasses the scheduler's eligibility
                gates and fires a real email through the production
                Resend integration. Useful for closing the debug loop
                after the 2026-06-14 reminder gap report. */}
            <button
              type="button"
              onClick={async () => {
                try {
                  const res = await axios.post(
                    `${API}/admin/send-test-reminder`,
                    { kind: 'morning_reminder' },
                    { withCredentials: true },
                  );
                  const d = res.data || {};
                  // Use the platform's toast lib if available; otherwise alert.
                  // eslint-disable-next-line no-alert
                  window.alert(
                    `Test reminder sent ✓\n\nTo: ${d.to_email}\n` +
                    `Kind: ${d.kind}\n` +
                    `Provider ID: ${(d.provider_id || '').slice(0,12)}…\n` +
                    `CTA URL in email: ${d.cta_url_in_email}\n\n` +
                    `Check your inbox in 30-60s.`
                  );
                } catch (e) {
                  // eslint-disable-next-line no-alert
                  window.alert(`Test reminder failed: ${e?.response?.data?.detail || e?.message || 'unknown'}`);
                }
              }}
              className="block w-full text-left"
              data-testid="send-test-reminder-btn"
            >
              <Card className="border border-emerald-100 rounded-2xl shadow-sm hover:shadow-md hover:border-emerald-300 transition-all cursor-pointer bg-emerald-50/30">
                <CardContent className="p-4 flex items-center gap-3">
                  <Activity className="h-4 w-4 text-emerald-600" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900">Send test reminder to me</p>
                    <p className="text-[11px] text-gray-500">Force-fires one email · bypasses all gates · 30s round-trip</p>
                  </div>
                  <ExternalLink className="h-3.5 w-3.5 text-emerald-400" />
                </CardContent>
              </Card>
            </button>
          </div>
        </Section>
      </div>
    </div>
  );
};

export default AdminDashboard;
